import React from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { ExternalLink, CheckCircle2 } from "lucide-react";

const getSteps = (isBulk) => [
  {
    title: "Log in to Stripe",
    text: (
      <>
        Go to{" "}
        <a
          href="https://dashboard.stripe.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent hover:underline inline-flex items-center"
        >
          Stripe Dashboard <ExternalLink className="ml-1 h-4 w-4" />
        </a>{" "}
        with your Stripe account.
      </>
    )
  },
  {
    title: "Add a Product",
    text: (
      <>
        In the left menu, click <strong>Products</strong>, then select{" "}
        <strong>+ Add product</strong>.
      </>
    )
  },
  {
    title: "Fill in Product Details",
    text: (
      <ul className="list-disc pl-6 mt-1 space-y-1 text-muted-foreground">
        <li>Give your product a clear name (e.g., Toyota Corolla Brakes).</li>
        <li>You can add an optional description for more details.</li>
        <li>
          Under <strong>Price</strong>, select <strong>One-time payment</strong>{" "}
          and enter your price (e.g., 199.99).
        </li>
      </ul>
    )
  },
  {
    title: "Save the Product",
    text: (
      <>
        Click <strong>Save product</strong> to finish adding it.
      </>
    )
  },
  {
    title: "Create a Payment Link",
    text: (
      <>
        On the product page, scroll to the <strong>Pricing</strong> section and
        click <strong>Create payment link</strong>.
      </>
    )
  },
  {
    title: "Set Checkout Options",
    text: (
      <>
        Choose whether buyers can adjust the quantity or other options during
        checkout.
      </>
    )
  },
  {
    title: "Generate the Link",
    text: (
      <>
        Click <strong>Create link</strong> and Stripe will give you a unique
        checkout link.
      </>
    )
  },
  {
    title: isBulk ? "Add Link to CSV File" : "Copy & Paste the Link",
    text: isBulk ? (
      <>
        Copy the generated link (looks like{" "}
        <code className="px-2 py-1 rounded bg-muted text-sm">
          https://buy.stripe.com/xxxx
        </code>
        ) and paste it into the <strong>Payment Link</strong> column of your CSV
        file for each part. <br />
        Once done, upload the updated CSV file.
      </>
    ) : (
      <>
        Copy the generated link (looks like{" "}
        <code className="px-2 py-1 rounded bg-muted text-sm">
          https://buy.stripe.com/xxxx
        </code>
        ) and paste it directly in your form so buyers can pay instantly.
      </>
    )
  }
];

const PaymentGenerateModal = ({ open, setOpen, isBulk }) => {
  const steps = getSteps(isBulk);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl scrollbar-none max-h-[85vh] overflow-y-auto rounded-2xl border border-border bg-gradient-to-b from-background to-muted/40 shadow-2xl p-6">
        <DialogHeader>
          <DialogTitle className="text-2xl text-accent font-bold flex items-center gap-2">
            {isBulk
              ? "How to Add Stripe Payment Links in Bulk"
              : "How to Generate a Stripe Payment Link"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {isBulk
              ? "Follow these steps to create payment links and add them into your CSV file before uploading."
              : "Follow these simple steps to create a reusable payment link. Your buyers can use this link anytime to pay for your product — no setup needed on their side."}
          </DialogDescription>
        </DialogHeader>

        <motion.div
          className="space-y-6 mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {steps.map((step, i) => (
            <motion.div
              key={i}
              className="rounded-xl border border-border bg-background/70 shadow-sm p-4 hover:shadow-md transition-all duration-300"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08 }}
            >
              <div className="flex items-start gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-white font-semibold shadow-md">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium text-foreground">{step.title}</p>
                  <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    {step.text}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          <motion.div
            className="p-5 mt-6 rounded-xl bg-accent/10 border border-accent/30 flex items-start gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <CheckCircle2 className="h-6 w-6 text-accent mt-0.5" />
            <p className="text-sm text-foreground leading-relaxed">
              ✅{" "}
              {isBulk
                ? "Your CSV can include payment links for multiple parts. Each link can be reused by buyers until disabled in Stripe."
                : "Your link can be shared with multiple buyers and reused as often as you need. It will only stop working if you disable it in Stripe."}
            </p>
          </motion.div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentGenerateModal;
